import { useEffect, useState } from 'react';

import type { ContentPage } from '../api/types';

import { fetchPageBySlug } from '../api/infoHubApi';
import { extractRenderableHtml } from '../utils/contentHtml';
import { normalizeSlug } from '../utils/contentPaths';

export type ContentPageState = 'loading' | 'ready' | 'notfound' | 'error';

export interface UseContentPageResult {
  page: ContentPage | null;
  /** Render-ready HTML, wrappers stripped. Empty when the page has no content yet. */
  html: string;
  state: ContentPageState;
}

/**
 * Loads a content page by its slug path and prepares its HTML for rendering.
 *
 * `notfound` and `error` are kept distinct: a missing or inactive page is an expected
 * outcome the visitor should see a friendly message for, not a failure worth logging.
 */
export const useContentPage = (rawSlug: string, preview?: boolean): UseContentPageResult => {
  const [page, setPage] = useState<ContentPage | null>(null);
  const [html, setHtml] = useState('');
  const [state, setState] = useState<ContentPageState>('loading');

  useEffect(() => {
    let cancelled = false;

    setState('loading');
    setPage(null);
    setHtml('');

    const slug = normalizeSlug(rawSlug);
    if (!slug) {
      setState('notfound');
      return;
    }

    const load = async (): Promise<void> => {
      try {
        const result = await fetchPageBySlug(slug, preview);
        if (cancelled) {
          return;
        }

        if (!result) {
          setState('notfound');
          return;
        }

        setPage(result);
        setHtml(extractRenderableHtml(result.contentHtml));
        setState('ready');
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error('Error loading content page:', error);
        setState('error');
      }
    };

    load();
    return (): void => {
      cancelled = true;
    };
  }, [rawSlug, preview]);

  return { page, html, state };
};
